import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  assemblePhotoBookPages,
  resolvePreviewImageSrc,
  resolveAllPagesForPdf,
  generatePhotoBookPdf,
  createPeechoCheckout,
  getPeechoQuote,
  PHOTO_BOOK_CHAPTER_TITLE,
  photoBookSectionTitle,
  padPagesToEven,
  trimUnneededFillers,
  PRINT_MIN_PAGES,
  htmlToPlainText,
  pageRoleLabel,
  lowQualityDetail,
  checkHtmlPhotoQuality,
  pagePrintCheck,
  applyPhotoBookLayout,
  photoBookLayoutFrom,
  withCoverDefault,
  coverPhotoChoices,
  makeCoverPage,
  makeBackCoverPage,
  MADE_WITH_TEXT,
} from "../../Common/PhotoBook";
import HTMLEditorModal from "./HTMLEditorModal";
import PromptModal from "./PromptModal";
import PhotoBookFlipViewer from "./PhotoBookFlipViewer";
import Section from "../../Models/Section";
import "./Modal.css";

// Same true-print-size-then-scale-down technique as PhotoBookFlipViewer.js's
// PRINT_PAGE_PX/PRINT_PADDING_PX (see its own comment for the full math/
// rationale) — reused here so the editor list's compact custom-page box
// shows correctly-proportioned content (an image's absolute px width sized
// relative to the real ~794px-wide page, not just stretched to fill
// whatever arbitrary rectangle this box happens to be) instead of a flat,
// unscaled render at a made-up 14px font. Found 2026-10-03, per explicit
// request for "a slightly more realistic page view."
const PRINT_PAGE_PX = (210 / 25.4) * 96;
const PRINT_PADDING_PX = (12 / 25.4) * 96;
const COMPACT_PAGE_PX = 220; // square, matches this box's old maxHeight

// Preview screen: assembles the page list and lets the user edit it
// (reorder/remove/crop/captions/text pages) before either previewing it
// (PhotoBookFlipViewer.js, screen-resolution images only) or ordering a
// real printed copy (which generates the actual print-quality PDF via
// resolveAllPagesForPdf, only at that point).
// The 3x3 focal-point grid's cells, in CSS object-position keyword form —
// matches them directly, no translation layer needed between "which cell
// was tapped" and what's actually applied to the <img>/sent to the PDF.
const CROP_POSITIONS = [
  ["top left", "top", "top right"],
  ["left", "center", "right"],
  ["bottom left", "bottom", "bottom right"],
];

// Safari older than 16.4 (an iPhone/iPad on iOS 16.3 or older, or an old
// Safari on a Mac) — Peecho's checkout page shows an empty order there.
// Why 16.4: the checkout is a Next.js 16.2 app (Safari 16.4+ supported) and
// its code uses class static blocks (`class K{static{…}}`), which Safari
// only understands from 16.4 — older Safari can't run it (checked
// 2026-10-17: iOS 15.8 empty, iOS 18.3 fine). Every iOS browser is Safari
// underneath, so the iOS version decides.
const OLDEST_SAFARI_FOR_CHECKOUT = 16.4;
function oldSafariForCheckout() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const ios = /(iPhone|iPad|iPod).*? OS (\d+)(?:_(\d+))?/.exec(ua);
  if (ios) return Number(`${ios[2]}.${ios[3] || 0}`) < OLDEST_SAFARI_FOR_CHECKOUT;
  if (/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/.test(ua)) return false;
  const safari = /Version\/(\d+)(?:\.(\d+))?.*Safari/.exec(ua);
  return !!safari && Number(`${safari[1]}.${safari[2] || 0}`) < OLDEST_SAFARI_FOR_CHECKOUT;
}

class PhotoBookPreviewModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pages: [],
      includeMaps: true,
      generating: false,
      generateError: null,
      quotingPrice: false,
      orderQuote: null,
      htmlEditorShow: false,
      htmlEditorHtml: "",
      htmlEditorPageIndex: null,
      promptShow: false,
      renameIndex: null,
      showFlipViewer: false,
      // Gates "Order Printed Book" — not forcing the user through every
      // single page (just requiring they've opened the preview at least
      // once), but re-armed (see _markPagesDirty) whenever the page set
      // actually changes afterward, so "I previewed this" always refers to
      // the CURRENT content, not some earlier edit of it. Added 2026-10-02
      // to avoid the egregious-error/refund-dispute risk of someone
      // ordering without ever having looked.
      hasViewedPreview: false,
      // "What's a cover/binding sheet?" explainer — shows the user's ACTUAL
      // current front/back cover thumbnails rather than an abstract diagram,
      // so it stays accurate to whatever they've actually arranged.
      showPageInfo: false,
    };
  }

  // Re-arms the preview gate (see hasViewedPreview above) — called at the
  // top of every handler that actually changes the page set/order/content,
  // so a post-preview edit can't silently order under cover of an earlier
  // preview. Also doubles as the "pages are locked" guard while a price
  // quote is being confirmed (_orderPrintedBook/_confirmOrderPrintedBook):
  // editing here would otherwise let the quoted page count/content drift
  // from what's actually submitted to Peecho between the two confirm
  // clicks. Found 2026-10-02 — the quote panel was a plain inline <div>,
  // not blocking the still-interactive page list rendered right below it.
  _markPagesDirty = () => {
    if (this.state.orderQuote) return false;
    // A Final check shows the file made before this edit — drop it.
    this.setState({ hasViewedPreview: false, finalCheck: null });
    return true;
  };

  // Read-only check for handlers that only OPEN something (the caption
  // editor, the add-text-page prompt) rather than mutate pages directly —
  // blocking the open avoids a confusing silent-discard later (the user
  // types a new caption, hits Save, and _saveCaption's own guard quietly
  // rejects it because a quote was still pending).
  _pagesLocked = () => !!this.state.orderQuote;

  // Screen-resolution images only (resolvePreviewImageSrc) — synchronous,
  // no network round trip beyond the browser's own <img> load. The print-
  // quality image is never fetched here; see resolveAllPagesForPdf, used
  // only once an order actually starts.
  componentDidMount() {
    this._loadPages(window.guide_obj?.photoBookLayout);
  }

  // Assembles the book and arranges it as last saved (layout null = as
  // assembled — "Start over").
  _loadPages = (layout) => {
    const assembled = assemblePhotoBookPages(window.guide_obj);
    this._coverDefaultHtml = assembled.coverDefaultHtml || null;
    const { pages, includeMaps } = applyPhotoBookLayout(assembled, layout, window.guide_obj);
    const mapped = pages.map((p) => {
      if (p.type === "map") return { ...p, src: p.imageUrl };
      if (p.type === "html" || p.type === "blank") return { ...p, src: null };
      return { ...p, src: resolvePreviewImageSrc(p.rawUrl) };
    });
    this._layoutLoaded = true;
    this.setState({ pages: mapped, includeMaps });
    this._checkPhotoQuality(mapped);
  };

  // Every change to the arrangement is kept with the guide (saved with it
  // on the next Save) — see applyPhotoBookLayout.
  componentDidUpdate(prevProps, prevState) {
    if (!this._layoutLoaded || !window.guide_obj) return;
    if (prevState.pages === this.state.pages && prevState.includeMaps === this.state.includeMaps) return;
    const trimmed = trimUnneededFillers(this.state.pages, this.state.includeMaps);
    if (trimmed) {
      this.setState({ pages: trimmed });
      return;
    }
    window.guide_obj.photoBookLayout = photoBookLayoutFrom(this.state.pages, this.state.includeMaps);
  }

  // Odd page count: a blank page just before the back cover, so the count
  // is even and the back cover stays a photo (2026-10-17).
  _addInsideBlank = () => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      let last = -1;
      pages.forEach((p, i) => { if (!p.excluded && (prev.includeMaps || p.type !== "map")) last = i; });
      pages.splice(last === -1 ? pages.length : last, 0, { type: "blank", blankId: `blank:${Date.now()}`, src: null });
      return { pages };
    });
  };

  // Under the printer's minimum: the filler blanks go in as real, visible
  // pages just before the back cover, so the user OKs them explicitly and
  // the preview shows them (2026-10-20).
  _addMinimumBlanks = () => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      let last = -1;
      let count = 0;
      pages.forEach((p, i) => { if (!p.excluded && (prev.includeMaps || p.type !== "map")) { last = i; count++; } });
      const stamp = Date.now();
      const blanks = Array.from({ length: Math.max(0, PRINT_MIN_PAGES - count) }, (_, k) => ({ type: "blank", blankId: `blank:filler-${stamp}-${k}`, src: null }));
      // One page is the front cover: the blanks go after it, not before
      // (it would become the back cover) — same as padPagesToEven.
      pages.splice(last === -1 ? pages.length : count === 1 ? last + 1 : last, 0, ...blanks);
      return { pages };
    });
  };

  // Odd page count, other choice: a visible blank page after the last page,
  // which becomes the (white) back cover (2026-10-20).
  _addBlankBackCover = () => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      let last = -1;
      pages.forEach((p, i) => { if (!p.excluded && (prev.includeMaps || p.type !== "map")) last = i; });
      pages.splice(last + 1, 0, { type: "blank", blankId: `blank:${Date.now()}`, src: null });
      return { pages };
    });
  };

  // Delete (2026-10-20): only pages the user made — custom and blank
  // pages. Photo and map pages come from the guide and would come back, so
  // they're left out with the checkbox instead. A custom page's saved text
  // section goes with it.
  // The designed back cover is left out with its checkbox, not deleted, so
  // it can come back.
  _canDeletePage = (page) => !!page && !page.isBackCover && (page.type === "blank" || (page.type === "html" && !page.autoCredits));

  // ── Designed covers (4.10.55) ──
  // Change photo: one tap on a photo of the book (coverPickerIndex = the
  // cover page being changed). Edit text / Made with: the back cover's
  // summary line and its "Made with Andiamo Places" line.
  _setCoverPhoto = (choice) => {
    const index = this.state.coverPickerIndex;
    this.setState({ coverPickerIndex: null });
    if (index == null || !choice || !this._markPagesDirty()) return;
    this.setState((prev) => ({
      pages: prev.pages.map((p, i) => {
        if (i !== index) return p;
        if (p.isCover) return { ...makeCoverPage(choice), src: resolvePreviewImageSrc(choice.rawUrl), captionHtml: p.captionHtml, sectionId: p.sectionId, chapterId: p.chapterId, excluded: p.excluded };
        if (p.isBackCover) return { ...makeBackCoverPage({ photo: choice, text: p.backText, madeWith: p.madeWith }), excluded: p.excluded };
        return p;
      }),
    }), () => this._checkPhotoQuality([this.state.pages[index]])); // the new photo gets its own check (2026-10-29)
  };

  _updateBackCover = (index, change) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => ({
      pages: prev.pages.map((p, i) =>
        i === index && p.isBackCover
          ? { ...makeBackCoverPage({ photo: p.backPhoto, text: change.text != null ? change.text : p.backText, madeWith: change.madeWith != null ? change.madeWith : p.madeWith }), excluded: p.excluded, lowQuality: p.lowQuality, lowQualityInfo: p.lowQualityInfo } // same photo: keeps its check
          : p
      ),
    }));
  };

  _backTextCallback = (inputs) => {
    const index = this.state.backTextIndex;
    this.setState({ backTextIndex: null });
    if (inputs === null || index == null) return;
    this._updateBackCover(index, { text: (inputs[0].value || "").trim() });
  };

  _renderCoverControls = (page, index) => {
    if (!page.isCover && !page.isBackCover) return null;
    const btn = { fontSize: 13, padding: "3px 10px", borderRadius: 6, border: "1px solid #CBD5E1", background: "#fff", cursor: "pointer", marginRight: 8 };
    return (
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, margin: "2px 0 8px" }}>
        <button type="button" style={btn} onClick={() => !this._pagesLocked() && this.setState({ coverPickerIndex: index })}>
          Change photo
        </button>
        {page.isBackCover ? (
          <>
            <button type="button" style={btn} onClick={() => !this._pagesLocked() && this.setState({ backTextIndex: index })}>
              Edit text
            </button>
            <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={page.madeWith !== false} onChange={(e) => !this._pagesLocked() && this._updateBackCover(index, { madeWith: e.target.checked })} />
              {MADE_WITH_TEXT}
            </label>
          </>
        ) : null}
      </div>
    );
  };

  _renderCoverPicker = () => {
    const index = this.state.coverPickerIndex;
    if (index == null) return null;
    const choices = coverPhotoChoices(window.guide_obj, this.state.pages);
    const current = this.state.pages[index];
    const currentUrl = current?.isCover ? current.rawUrl : current?.backPhoto?.rawUrl;
    return (
      <Modal show onHide={() => this.setState({ coverPickerIndex: null })} centered size="lg" style={{ zIndex: 70000 }}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>{current?.isCover ? "Front cover photo" : "Back cover photo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginTop: 0 }}>Tap a photo to use it.</p>
          <div className="pb-cover-picker" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
            {choices.map((c) => (
              <button
                key={c.rawUrl}
                type="button"
                className="pb-cover-choice"
                title={c.place?.Name || "Guide picture"}
                aria-label={`Use ${c.place?.Name || "the guide picture"}`}
                onClick={() => this._setCoverPhoto(c)}
                style={{ padding: 0, border: c.rawUrl === currentUrl ? "3px solid #2563EB" : "1px solid #ddd", borderRadius: 6, overflow: "hidden", aspectRatio: "1 / 1", background: "#f1f5f9", cursor: "pointer" }}
              >
                <img src={resolvePreviewImageSrc(c.rawUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  _deletePage = (index) => {
    const page = this.state.pages[index];
    if (!this._canDeletePage(page)) return;
    const hasContent = page.type === "html" && (/<img/i.test(page.captionHtml || "") || htmlToPlainText(page.captionHtml || "").trim() !== "");
    if (hasContent && !window.confirm("Delete this page and its text?")) return;
    if (!this._markPagesDirty()) return;
    if (page.sectionId) {
      const chapter = (window.guide_obj.chapters || []).find((c) => c.ID === page.chapterId);
      const section = chapter && (chapter.sections || []).find((x) => x.ID === page.sectionId);
      if (section) window.guide_obj.deleteSection(page.chapterId, section);
    }
    this.setState((prev) => ({ pages: prev.pages.filter((p) => p !== page) }));
  };

  _startOver = () => {
    if (!this._markPagesDirty()) return;
    if (!window.confirm("Start the book over? Page order, crops, pages left out and your custom pages are reset (captions on photos and maps stay).")) return;
    window.guide_obj.photoBookLayout = null;
    this._loadPages(null);
  };

  // One-time, lightweight metadata-only quality probe per photo (see
  // checkPhotoQuality's own comment — no full download, unlike the real
  // print-resolution resolve at order time) — kicked off once here, not on
  // every render. Matches updates back by object identity rather than
  // rawUrl/index — rawUrl alone would collide for every custom ("html")
  // page, which has no rawUrl of its own (all `undefined === undefined`),
  // and a plain index would go stale if the user reorders/removes pages
  // before a probe resolves. Page objects are only ever replaced wholesale
  // (never mutated in place) elsewhere in this component, so `===` stays a
  // reliable match. Extended 2026-10-04 to also check a custom page's
  // embedded photos (see checkHtmlPhotoQuality) — previously only
  // type:"photo" pages were checked, silently missing low-res images a user
  // embedded via the custom-page HTML editor.
  _checkPhotoQuality = (pages) => {
    pages.forEach((page) => {
      // A photo page keeps what was found (size, kind of photo) so the
      // warning can say it (2026-10-28).
      const check = pagePrintCheck(page);
      if (!check) return;
      check.then((info) => {
        if (!info || info.low !== true) return;
        this.setState((prev) => ({
          pages: prev.pages.map((p) => (p === page ? { ...p, lowQuality: true, lowQualityInfo: info } : p)),
        }));
      });
    });
  };

  // Reorder/remove/crop all operate on the full pages array by its real
  // index (passed through from _renderPage's closure), not the filtered
  // "Include maps"-aware list index — so a toggle-off/on doesn't scramble
  // which page a button actually affects.
  _movePage = (index, delta) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      const target = index + delta;
      if (target < 0 || target >= pages.length) return null;
      [pages[index], pages[target]] = [pages[target], pages[index]];
      return { pages };
    });
  };

  // One-click-at-a-time reordering doesn't scale to a long book (moving
  // page 2 to the back of a 40-page book took 38 clicks) — jump straight to
  // the front/back instead. Found 2026-10-03.
  _movePageToStart = (index) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      const [moved] = pages.splice(index, 1);
      pages.unshift(moved);
      return { pages };
    });
  };

  _movePageToEnd = (index) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      const [moved] = pages.splice(index, 1);
      pages.push(moved);
      return { pages };
    });
  };

  // Toggles whether a page is included in the actual book — replaces the
  // old permanent delete (_removePage), since removing a page by mistake
  // (or changing your mind later) meant starting the whole arrangement
  // over. An excluded page stays in the list (dimmed, no role label since
  // it's not really "in the book") so it can be re-included just as easily.
  // Found 2026-10-03.
  _toggleIncludePage = (index) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      pages[index] = { ...pages[index], excluded: !pages[index].excluded };
      return { pages };
    });
  };

  // "Exclude low-res images" (2026-10-18): excludes (or re-includes) every
  // photo page flagged low resolution in one go — the same exclusion as
  // each page's own toggle, so they stay in the list, dimmed. Custom pages
  // with an embedded low-res photo are left to the user.
  _setExcludeLowRes = (exclude) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => ({
      pages: prev.pages.map((p) => (p.type === "photo" && p.lowQuality && !!p.excluded !== exclude ? { ...p, excluded: exclude } : p)),
    }));
  };

  // Measures a custom page's content against the real print page's exact
  // content box (210mm square minus buildBookHtml's 12mm padding each
  // side) via a hidden, identically-sized+styled div rendered alongside
  // the compact visible preview (see its own comment). scrollHeight always
  // reflects the full un-clipped content height even though this div has
  // overflow:hidden, so comparing it to clientHeight (the box's actual
  // fixed height) is a reliable overflow check — mm is a logical CSS unit,
  // so this stays accurate regardless of the viewer's screen pixel
  // density. Runs on every render (a new ref callback identity each time,
  // same as React always does for inline arrow-function refs) but only
  // calls setState when the computed value actually changes, so it can't
  // loop. Found 2026-10-03.
  _measureCustomPageOverflow = (index, el) => {
    if (!el) return;
    const overflows = el.scrollHeight > el.clientHeight + 1; // +1: sub-pixel rounding tolerance
    if (this.state.pages[index]?.contentOverflows === overflows) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      pages[index] = { ...pages[index], contentOverflows: overflows };
      return { pages };
    });
  };

  _setCropPosition = (index, objectPosition) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      pages[index] = { ...pages[index], objectPosition };
      return { pages };
    });
  };

  // The auto-derived label a page would show with no customization —
  // also doubles as the seed content when first opening the editor and as
  // the human-readable part of a new Section's Title.
  _autoLabel(page) {
    if (page.type === "blank") return "Blank page";
    if (page.type === "map") return `Map — ${page.caption || ""}`;
    if (page.type === "html") return page.title || "Untitled page";
    return page.place?.Name || "Untitled place";
  }

  // List rows show a quick plain-text snippet, not a full rich-HTML render
  // (project plan: full rendering only happens in the editor itself and in
  // the final PDF — a list row doesn't need a WebView-grade renderer for a
  // line or two of text).
  // A photo credits page's short name in the list (2026-10-20): its
  // heading, e.g. "Photo credits (1 of 7)" — not all of its credits text.
  _creditsLabel(page) {
    const m = (page.captionHtml || "").match(/<h3[^>]*>([\s\S]*?)<\/h3>/i);
    return m ? m[1].replace(/<[^>]*>/g, "").trim() : page.title || "Photo credits";
  }

  _captionPreview(page, index) {
    if (page.captionHtml === undefined && index === this._frontCoverIndex() && (page.type === "photo" || page.type === "map") && this._coverDefaultHtml)
      return `${htmlToPlainText(this._coverDefaultHtml).replace(/\n+/g, " ")} (cover title)`;
    if (!page.captionHtml) return this._autoLabel(page);
    const text = htmlToPlainText(page.captionHtml).replace(/\n+/g, " ");
    return text.length > 0 ? text : this._autoLabel(page);
  }

  // Pencil icon — opens the HTML editor seeded with whatever's already
  // there (a previous custom caption) or a simple default wrapping the
  // auto label, so there's always something sensible to start editing.
  _editCaption = (index) => {
    if (this._pagesLocked()) return;
    const page = this.state.pages[index];
    if (page?.isBackCover) {
      this.setState({ backTextIndex: index });
      return;
    }
    const coverDefault = page.captionHtml === undefined && index === this._frontCoverIndex() ? this._coverDefaultHtml : null;
    // A custom page starts empty — its title is only its name in the list,
    // never added to the page itself (2026-10-20).
    const initialHtml = page.type === "html" || page.type === "blank" ? page.captionHtml || "" : page.captionHtml || coverDefault || `<p>${this._autoLabel(page)}</p>`;
    this.setState({ htmlEditorShow: true, htmlEditorHtml: initialHtml, htmlEditorPageIndex: index });
  };

  // Ensures the "Photo Book" chapter exists, then creates or updates the
  // Section backing this page's caption — same addSection/modifySection
  // calls SectionsDialog.js already makes elsewhere in the app, nothing
  // new. Photo/map pages get an identity-prefixed Title so a later
  // assemblePhotoBookPages call can auto-reattach this same caption (see
  // PhotoBook.js's findPhotoBookCaptionSection); text-only pages have no
  // stable identity (session-only, per project plan) so just use their title.
  _saveCaption = (html) => {
    if (!this._markPagesDirty()) return;
    const index = this.state.htmlEditorPageIndex;
    let page = this.state.pages[index];
    // Content typed into a blank page turns it into a custom page
    // (2026-10-20) — a blank prints nothing, so its edits were lost.
    if (page.type === "blank" && html && html.trim()) {
      page = { type: "html", title: "Custom page", textId: `text:${Date.now()}`, captionHtml: "", excluded: page.excluded };
    }
    let chapter = window.guide_obj.findChapterByName(PHOTO_BOOK_CHAPTER_TITLE);
    if (!chapter) chapter = window.guide_obj.addChapter(PHOTO_BOOK_CHAPTER_TITLE);

    let sectionId = page.sectionId;
    let chapterId = page.chapterId;
    if (sectionId) {
      const existing = chapter.sections.find((s) => s.ID === sectionId);
      const newSection = { ...existing, html };
      window.guide_obj.modifySection(chapterId, existing, newSection);
    } else {
      const label = this._autoLabel(page);
      const section = new Section(window.guide_obj.chapters, photoBookSectionTitle(page, label));
      section.html = html;
      window.guide_obj.addSection(chapter.ID, section);
      sectionId = section.ID;
      chapterId = chapter.ID;
    }

    this.setState((prev) => {
      const pages = prev.pages.slice();
      pages[index] = { ...(page.textId && pages[index].type === "blank" ? page : pages[index]), captionHtml: html, sectionId, chapterId };
      return { pages, htmlEditorShow: false, htmlEditorPageIndex: null };
    });
    this._recheckHtmlPage(page, html);
  };

  // A custom page is checked again whenever it's saved (2026-10-20) — the
  // open-time check never saw images added in the editor afterwards. Found
  // by textId, since the page list may change while the check runs.
  _recheckHtmlPage = (page, html) => {
    if (page.type !== "html" || !page.textId) return;
    checkHtmlPhotoQuality(html).then((lowQuality) => {
      this.setState((prev) => ({
        pages: prev.pages.map((p) => (p.textId === page.textId ? { ...p, lowQuality: lowQuality === true } : p)),
      }));
    });
  };

  // "+ Add Custom Page" — prompts for a short title (used as the Section's
  // identifying name, same PromptModal SectionsDialog.js already uses for
  // naming), appends a new no-image page, then opens the editor immediately
  // for first content entry.
  _addCustomPage = (atBack = false) => {
    if (this._pagesLocked()) return;
    this.setState({ promptShow: true, customPageAtBack: atBack === true });
  };

  // A custom page's name is its own (2026-10-20): shown in the list and
  // renamed here, never taken from or added to its content. Its saved
  // section ("Text: <name>") is renamed to match.
  _renamePageCallback = (inputs) => {
    const index = this.state.renameIndex;
    this.setState({ renameIndex: null });
    if (inputs === null || index == null) return;
    const title = inputs[0].value?.trim();
    if (!title) return;
    if (!this._markPagesDirty()) return;
    const page = this.state.pages[index];
    if (page?.sectionId) {
      const chapter = (window.guide_obj.chapters || []).find((c) => c.ID === page.chapterId);
      const section = chapter && (chapter.sections || []).find((x) => x.ID === page.sectionId);
      if (section) window.guide_obj.modifySection(page.chapterId, section, { ...section, Title: `Text: ${title}` });
    }
    this.setState((prev) => ({ pages: prev.pages.map((p, i) => (i === index ? { ...p, title } : p)) }));
  };

  _addCustomPageCallback = (inputs) => {
    this.setState({ promptShow: false });
    if (inputs === null) return;
    const title = inputs[0].value?.trim();
    if (!title) return;
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      // At the front, not appended — a text page is most often a title/
      // cover page (confirmed by the user's own first real use being "Our
      // Trip To Tuscany"), and the existing reorder controls already let
      // it be moved anywhere else afterward. Found 2026-10-02.
      // From the odd-count box (2026-10-20): just before the back cover, so
      // it evens the count without taking the front cover.
      let at = 0;
      if (prev.customPageAtBack) {
        at = pages.length;
        pages.forEach((p, i) => { if (!p.excluded && (prev.includeMaps || p.type !== "map")) at = i; });
      }
      pages.splice(at, 0, { type: "html", title, captionHtml: "", textId: `text:${Date.now()}` });
      return {
        pages,
        customPageAtBack: false,
        htmlEditorShow: true,
        htmlEditorHtml: "",
        htmlEditorPageIndex: at,
      };
    });
  };

  // "Blank cover" checkbox — some users want a plain blank front cover
  // instead of whatever photo/map would otherwise land in that slot (a
  // place a note alone can't fix, since nothing else in this screen lets
  // you actually create one). Inserts/removes a real `{type:"blank"}` page
  // at the very front, tagged manualCover so this toggle (not the separate,
  // computed-only padPagesToEven back-of-book blank) is the only thing that
  // adds or removes it. Found 2026-10-03, alongside the multi-destination
  // overview map now also taking the front-cover slot.
  _toggleBlankCover = (checked) => {
    if (!this._markPagesDirty()) return;
    this.setState((prev) => {
      const pages = prev.pages.slice();
      const idx = pages.findIndex((p) => p.type === "blank" && p.manualCover);
      if (checked) {
        if (idx === -1) pages.unshift({ type: "blank", manualCover: true });
      } else if (idx !== -1) {
        pages.splice(idx, 1);
      }
      return { pages };
    });
  };

  // Page count is known the moment this screen opens, well before anyone
  // clicks Generate — a free, no-compute way to let the user bail before
  // spending a Cloud Function invocation, especially once a real per-page
  // Peecho price can be shown here too (today just the count, until real
  // pricing exists). window.confirm is synchronous/blocking (unlike an
  // async call), so it doesn't break the popup-blocker fix below — the
  // browser still sees window.open() as part of the same click gesture.
  // The default cover title goes on whatever is the front cover now (see
  // withCoverDefault) — used by the flip preview and the order.
  _visiblePages = () => {
    const { pages, includeMaps } = this.state;
    return withCoverDefault(pages.filter((p) => !p.excluded && (includeMaps || p.type !== "map")), this._coverDefaultHtml);
  };

  // The page index (in state.pages) that is the front cover now.
  _frontCoverIndex = () => {
    const { pages, includeMaps } = this.state;
    return pages.findIndex((p) => !p.excluded && (includeMaps || p.type !== "map"));
  };

  // Opens the page-flip viewer (PhotoBookFlipViewer.js) — replaces the old
  // "Generate PDF (preview)" button, which handed out a direct link to the
  // exact full-resolution print file (a real revenue leak: anyone could
  // save and print that file anywhere). This only ever shows the already-
  // resolved screen-resolution `src` images set in componentDidMount.
  _previewBook = () => {
    this.setState({ showFlipViewer: true });
  };

  // Writes a plain loading message into an already-open blank tab — without
  // this it just sits blank/white for however long resolve+generate(+Peecho)
  // take (several seconds to half a minute for a real trip), which reads as
  // "did nothing happen" rather than "still working." Found 2026-10-01 (a
  // user testing the real order flow waited on a blank tab, switched back to
  // the app, and only then saw it resolve).
  _writeLoadingTab(win, message) {
    if (!win || !win.document) return;
    win.document.write(
      `<!DOCTYPE html><html><head><title>Andiamo Photo Book</title></head>` +
        `<body style="font-family:system-ui,Arial,sans-serif;text-align:center;padding:60px 20px;color:#333;">` +
        `<h2>${message}</h2><p>This can take up to 30 seconds — please don't close this tab.</p></body></html>`
    );
  }

  // Peecho's Publication/Print Button API (hosted checkout — the end
  // customer pays Peecho directly, not us; see createPeechoCheckout's own
  // comment in PhotoBook.js). Generates the PDF exactly like _doGeneratePdf
  // above, then hands its public URL straight to Peecho rather than
  // opening it — this is the actual order path, not just a PDF sanity
  // check.
  //
  // Split into two real clicks rather than one window.open()-then-await-
  // then-window.confirm() flow: window.open() shifts browser focus to the
  // new tab, so a window.confirm() that pops up afterward in THIS (opener)
  // tab can go unseen behind it — reads as "stuck" on the new tab's loading
  // message, and window.focus() is not reliably honored for a call that
  // isn't itself directly inside a synchronous user-gesture handler (it
  // runs after an await here). Fixed 2026-10-01 by moving the price lookup
  // and confirmation entirely in-page (first click, no tab opened yet, no
  // native dialog that can hide) and only opening the tab on the SECOND
  // click (_confirmOrderPrintedBook), which is a fresh, real, synchronous
  // user gesture — actually a more solid popup-blocker exemption than
  // before, not a weaker one.
  _orderPrintedBook = async () => {
    // As printed (padPagesToEven) — the blank pages are placed here, not left
    // to the print server, which can't keep a designed back cover last.
    const pages = padPagesToEven(this._visiblePages());
    this.setState({ quotingPrice: true, orderQuote: null, generateError: null });
    const quote = await getPeechoQuote(pages.length);
    this.setState({ quotingPrice: false, orderQuote: { pages, quote } });
  };

  _cancelOrderPrintedBook = () => {
    this.setState({ orderQuote: null });
  };

  // The actual popup-blocker-safe tab open — directly inside this click
  // handler, no awaits before it.
  // Ordering is two steps (2026-10-19): first the print file is made and
  // its pages are shown as small pictures ("Final check" — the real file,
  // not the preview); only "Looks right" creates the Peecho order. Pages
  // 11–12 of a real order printed blank before the server checked images.
  _confirmOrderPrintedBook = async () => {
    const { pages } = this.state.orderQuote;
    this.setState({ orderQuote: null, oldBrowserCheckoutUrl: null, finalCheck: null });
    const guideId = this.props.guide_info_id;
    if (!guideId) {
      this.setState({ generateError: "This guide hasn't been saved yet — save it first." });
      return;
    }
    this.setState({ generating: true, generateError: null });
    try {
      const resolved = await resolveAllPagesForPdf(pages, this.props.user_info?.uid, guideId);
      const made = await generatePhotoBookPdf(guideId, this.props.user_info?.uid, resolved);
      this.setState({
        finalCheck: {
          pdfUrl: made.url,
          coverUrl: made.coverUrl,
          proofUrls: made.proofUrls || [],
          pageCount: resolved.length,
          firstImage: resolved[0]?.imageUrl,
        },
      });
    } catch (e) {
      this.setState({ generateError: e.message });
    } finally {
      this.setState({ generating: false });
    }
  };

  _continueToCheckout = async () => {
    const fc = this.state.finalCheck;
    if (!fc) return;
    const guideId = this.props.guide_info_id;
    const showLinkOnly = oldSafariForCheckout();
    // Opened right here, in the click, so the browser allows the new tab.
    const checkoutWindow = showLinkOnly ? null : window.open("", `photobook_checkout_${Date.now()}`);
    if (checkoutWindow) this._writeLoadingTab(checkoutWindow, "Opening the printer's checkout…");
    this.setState({ generating: true, generateError: null });
    try {
      const checkoutUrl = await createPeechoCheckout(
        guideId,
        fc.pdfUrl,
        fc.pageCount,
        window.guide_obj?.attributes?.guide_name || "Andiamo Trip Photo Book",
        fc.coverUrl || fc.firstImage
      );
      this.setState({ finalCheck: null });
      if (showLinkOnly) this.setState({ oldBrowserCheckoutUrl: checkoutUrl });
      else if (checkoutWindow) checkoutWindow.location.href = checkoutUrl;
      else window.open(checkoutUrl, "_blank");
    } catch (e) {
      if (checkoutWindow) checkoutWindow.close();
      this.setState({ generateError: e.message });
    } finally {
      this.setState({ generating: false });
    }
  };

  _renderFinalCheck() {
    const fc = this.state.finalCheck;
    if (!fc) return null;
    const pics = fc.proofUrls || [];
    return (
      <div style={{ marginTop: 10, padding: 12, border: "1px solid #3598DB", borderRadius: 8, background: "#F4F9FD" }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Final check — this is what will be printed</div>
        <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
          {pics.length > 0
            ? `All ${pics.length} pages of the print file, in order (page 1 is the front cover, the last is the back cover). Check every photo is there before you pay.`
            : "The page pictures couldn't be made this time, but the print file was checked: every photo loaded."}
        </div>
        {pics.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8, maxHeight: 420, overflowY: "auto", padding: 2 }}>
            {pics.map((src, i) => (
              <figure key={src} style={{ margin: 0, textAlign: "center" }}>
                <img src={src} alt={`Page ${i + 1}`} loading="lazy" style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", border: "1px solid #ccc", borderRadius: 3, background: "#fff" }} />
                <figcaption style={{ fontSize: 11, color: "#666" }}>{i === 0 ? "Front" : i === pics.length - 1 ? "Back" : i + 1}</figcaption>
              </figure>
            ))}
          </div>
        ) : null}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          <button className="btn btn-success btn-sm" onClick={this._continueToCheckout} disabled={this.state.generating}>
            Looks right — continue to checkout
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => this.setState({ finalCheck: null })} disabled={this.state.generating}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  _renderOldBrowserCheckout() {
    const url = this.state.oldBrowserCheckoutUrl;
    if (!url) return null;
    const email = this.props.user_info?.email || "";
    const title = window.guide_obj?.attributes?.guide_name || "Photo Book";
    const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`Photo Book order — ${title}`)}&body=${encodeURIComponent(`Open this link on a computer, tablet or newer phone to finish ordering your photo book:\n\n${url}`)}`;
    return (
      <div style={{ margin: "8px 0", padding: 12, border: "1px solid #F59E0B", background: "#FFFBEB", borderRadius: 8, fontSize: 13, color: "#78350F" }}>
        <b>Your book is ready to order.</b> The printer's checkout page doesn't work on this version of Safari (it needs iOS / Safari 16.4 or
        newer — here it shows an empty order). Open the link on a computer, tablet or newer phone to pay — it stays valid.
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          <button className="btn btn-sm btn-primary" onClick={() => { try { navigator.clipboard.writeText(url); } catch (e) { /* ignore */ } }}>Copy link</button>
          <a className="btn btn-sm btn-outline-primary" href={mailto}>Email me the link</a>
          <a className="btn btn-sm btn-outline-secondary" href={url} target="_blank" rel="noreferrer">Open anyway</a>
          <button className="btn btn-sm btn-link" onClick={() => this.setState({ oldBrowserCheckoutUrl: null })}>Close</button>
        </div>
      </div>
    );
  }

  _renderCropGrid(page, index) {
    return (
      <div style={{ display: "inline-grid", gridTemplateColumns: "repeat(3, 22px)", gridGap: 2 }}>
        {CROP_POSITIONS.flat().map((pos) => (
          <div
            key={pos}
            onClick={() => this._setCropPosition(index, pos)}
            title={pos}
            style={{
              width: 22,
              height: 22,
              border: "1px solid #ccc",
              borderRadius: 3,
              cursor: "pointer",
              background: (page.objectPosition || "center") === pos ? "#3598DB" : "#f2f2f2",
            }}
          />
        ))}
      </div>
    );
  }

  // Small labeled thumbnail for the "how pages become a book" explainer —
  // shows the user's ACTUAL current front/back cover content (photo, map, a
  // text page's title, or the synthetic blank filler from an odd page
  // count) rather than a generic diagram, so it's always accurate to
  // whatever they've actually arranged. Reuses _captionPreview (same
  // custom-caption-or-auto-label logic the editable list's rows already
  // use) so the cover's caption text — e.g. the auto-populated guide
  // name/dates on page 1 — actually shows here, not just the bare photo.
  // Found 2026-10-02: the first version only rendered the image/title,
  // silently dropping the one thing this panel exists to preview.
  _renderCoverThumb(page, label) {
    const captionText = page && page.type !== "blank" ? this._captionPreview(page) : "";
    const centerStyle = { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" };
    return (
      <div>
        <div
          style={{
            width: 90,
            height: 90,
            borderRadius: 6,
            border: "1px solid #ddd",
            background: "#f2f2f2",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {page?.type === "blank" || !page ? (
            <div style={centerStyle}>
              <span style={{ fontSize: 11, color: "#999" }}>Blank</span>
            </div>
          ) : page.type === "html" ? (
            <div style={{ ...centerStyle, padding: 6 }}>
              <span style={{ fontSize: 10, color: "#333", lineHeight: 1.3 }}>{captionText}</span>
            </div>
          ) : page.src ? (
            <>
              <img src={page.src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { if (page.rawUrl && e.currentTarget.src !== page.rawUrl) e.currentTarget.src = page.rawUrl; }} />
              {captionText ? (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "14px 4px 4px",
                    background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
                  }}
                >
                  <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }}>{captionText}</span>
                </div>
              ) : null}
            </>
          ) : (
            <div style={centerStyle}>
              <span style={{ fontSize: 11, color: "#999" }}>Loading…</span>
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#3598DB", marginTop: 4, textTransform: "uppercase", textAlign: "center" }}>
          {label}
        </div>
      </div>
    );
  }

  render() {
    const {
      pages,
      includeMaps,
      generating,
      generateError,
      quotingPrice,
      orderQuote,
      htmlEditorShow,
      promptShow,
      showFlipViewer,
      hasViewedPreview,
      showPageInfo,
    } = this.state;

    if (showFlipViewer)
      return (
        <PhotoBookFlipViewer
          show={showFlipViewer}
          onHide={() => this.setState({ showFlipViewer: false, hasViewedPreview: true })}
          pages={padPagesToEven(this._visiblePages())}
        />
      );
    if (htmlEditorShow)
      return (
        <HTMLEditorModal
          show={htmlEditorShow}
          onHide={() => this.setState({ htmlEditorShow: false, htmlEditorPageIndex: null })}
          html={this.state.htmlEditorHtml}
          callback={this._saveCaption}
          callback_rt={null}
        />
      );
    if (this.state.backTextIndex != null)
      return (
        <PromptModal
          show
          onHide={() => this.setState({ backTextIndex: null })}
          PromptModalTitle="Back cover text"
          PromptModalMessage="The line under the photo on the back cover"
          PromptModalButtons={[
            { Title: "OK", Method: this._backTextCallback },
            { Title: "CANCEL", Method: null },
          ]}
          PromptModalInputs={[{ name: "Text", value: this.state.pages[this.state.backTextIndex]?.backText || "" }]}
        />
      );
    if (this.state.renameIndex != null)
      return (
        <PromptModal
          show
          onHide={() => this.setState({ renameIndex: null })}
          PromptModalTitle="Rename Page"
          PromptModalMessage="The page's name in this list — it isn't printed"
          PromptModalButtons={[
            { Title: "OK", Method: this._renamePageCallback },
            { Title: "CANCEL", Method: null },
          ]}
          PromptModalInputs={[{ name: "Title", value: this.state.pages[this.state.renameIndex]?.title || "" }]}
        />
      );
    if (promptShow)
      return (
        <PromptModal
          show={promptShow}
          onHide={() => this.setState({ promptShow: false })}
          PromptModalTitle="Add Custom Page"
          PromptModalMessage="A name for this page in the list (used to find it again later) — it isn't printed"
          PromptModalButtons={[
            { Title: "OK", Method: this._addCustomPageCallback },
            { Title: "CANCEL", Method: null },
          ]}
          PromptModalInputs={[{ name: "Title", value: "" }]}
        />
      );

    const visiblePages = this._visiblePages();
    const hasMapPages = pages.some((p) => p.type === "map");
    const paddedPages = padPagesToEven(visiblePages);
    const totalPadded = paddedPages.length;
    const isOddCount = visiblePages.length % 2 !== 0;
    // Printed books need an even page count; below the minimum the filler
    // blanks already make it even. An odd count blocks ordering until the
    // user picks where the extra page goes (2026-10-20).
    // Not with a designed back cover: its blank page goes in by itself.
    const needsEvenChoice = isOddCount && visiblePages.length >= PRINT_MIN_PAGES && !visiblePages[visiblePages.length - 1]?.isBackCover;
    const missingPages = visiblePages.length > 0 ? Math.max(0, PRINT_MIN_PAGES - visiblePages.length) : 0;
    const frontCoverPage = paddedPages[0];
    const backCoverPage = paddedPages[totalPadded - 1];
    return (
      <div className="modal">
        {this._renderCoverPicker()}
        <Modal {...this.props} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable={true}>
          <Modal.Header>
            <div style={{ width: "100%" }}>
              <div className="dialog-heading">
                <div>
                  <h4 style={{ color: "#3598DB" }}>Photo Book Preview</h4>
                </div>
                <div>
                  <FontAwesomeIcon
                    icon={solid("times")}
                    className="pointer color-blue"
                    onClick={() => this.props.onHide()}
                  ></FontAwesomeIcon>
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body ref={(r) => { this._bodyEl = r; }}>
            {pages.length > 0 ? (
              <div style={{ marginBottom: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={this._previewBook}>
                  Preview Book
                </button>
                <FontAwesomeIcon
                  icon={solid("circle-info")}
                  className="pointer color-blue"
                  title="How pages map to the printed book's cover and binding"
                  style={{ marginLeft: 10, marginRight: 2 }}
                  onClick={() => this.setState((st) => ({ showPageInfo: !st.showPageInfo }))}
                />
                <button
                  className="btn btn-success btn-sm"
                  // Explicit gray override when disabled, not just Bootstrap's
                  // default :disabled opacity dimming — that alone still read
                  // as a clickable green button (confirmed misleading).
                  // Matches mobile's identical generateButtonDisabled style
                  // (an explicit neutral background, not opacity). Found
                  // 2026-10-03.
                  style={
                    generating || quotingPrice || !!orderQuote || !!this.state.finalCheck || !hasViewedPreview || needsEvenChoice || missingPages > 0
                      ? { marginLeft: 8, backgroundColor: "#ADB5BD", borderColor: "#ADB5BD", color: "#fff" }
                      : { marginLeft: 8 }
                  }
                  onClick={this._orderPrintedBook}
                  disabled={generating || quotingPrice || !!orderQuote || !!this.state.finalCheck || !hasViewedPreview || needsEvenChoice || missingPages > 0}
                  title={!hasViewedPreview ? "Preview the book first — see the button to the left" : needsEvenChoice || missingPages > 0 ? "Choose how to fill the book first — see below" : undefined}
                >
                  {quotingPrice ? "Getting price…" : generating ? "Preparing the book…" : "Order Printed Book"}
                </button>
                {missingPages > 0 ? (
                  <div style={{ marginTop: 8, padding: 8, border: "1px solid #F59E0B", borderRadius: 6, background: "#FFFBEB", fontSize: 13 }}>
                    <div style={{ marginBottom: 6 }}>
                      Your book has {visiblePages.length} page{visiblePages.length === 1 ? "" : "s"}. Printed books have at least {PRINT_MIN_PAGES} pages, and are priced as {PRINT_MIN_PAGES} pages.
                    </div>
                    <button className="btn btn-outline-primary btn-sm" style={{ marginBottom: 4 }} onClick={this._addMinimumBlanks}>
                      Add {missingPages} blank page{missingPages === 1 ? "" : "s"} before the back cover
                    </button>
                    <div style={{ color: "#71717A", fontSize: 12 }}>
                      Or add pages yourself (photos, maps or custom pages) to use them.
                    </div>
                  </div>
                ) : null}
                {oldSafariForCheckout() ? (
                  // Up front (2026-10-22), not only at checkout: this device
                  // can't run the printer's checkout.
                  <div style={{ marginTop: 8, padding: 8, border: "1px solid #93C5FD", borderRadius: 6, background: "#EFF6FF", fontSize: 13 }}>
                    The printer's checkout needs iOS 16.4 or later, and this device is older. You can still design and preview your book here; when you order, you'll get a link to finish the checkout on another device.
                  </div>
                ) : null}
                {needsEvenChoice ? (
                  <div style={{ marginTop: 8, padding: 8, border: "1px solid #F59E0B", borderRadius: 6, background: "#FFFBEB", fontSize: 13 }}>
                    <div style={{ marginBottom: 6 }}>
                      Your book has {visiblePages.length} pages. Printed books need an even number of pages — choose one:
                    </div>
                    <button className="btn btn-outline-primary btn-sm" style={{ marginRight: 6, marginBottom: 4 }} onClick={this._addInsideBlank}>
                      Add a blank page inside
                    </button>
                    <button className="btn btn-outline-primary btn-sm" style={{ marginRight: 6, marginBottom: 4 }} onClick={this._addBlankBackCover}>
                      Make the back cover blank
                    </button>
                    <button className="btn btn-outline-primary btn-sm" style={{ marginBottom: 4 }} onClick={() => this._addCustomPage(true)}>
                      Add a custom page
                    </button>
                    <div style={{ color: "#71717A", fontSize: 12 }}>
                      A custom page (your own title and text) goes just before the back cover. Or make the count even yourself: untick a page to leave it out, delete a custom or blank page, or add one. The last page in the list is the back cover.
                    </div>
                  </div>
                ) : null}
                {!hasViewedPreview ? (
                  <div style={{ marginTop: 6, color: "#71717A", fontSize: 12 }}>
                    Preview the book above before ordering — makes sure what prints is what you expect.
                  </div>
                ) : null}
                {generateError ? <div style={{ color: "#C2410C", marginTop: 6 }}>{generateError}</div> : null}
                {this._renderOldBrowserCheckout()}
                {this._renderFinalCheck()}
                {orderQuote ? (
                  <div style={{ marginTop: 10, padding: 10, border: "1px solid #ddd", borderRadius: 6, background: "#f9f9f9" }}>
                    <div style={{ marginBottom: 8 }}>
                      {orderQuote.quote
                        ? `This book will have ${orderQuote.pages.length} page${orderQuote.pages.length === 1 ? "" : "s"}. Product price: ${orderQuote.quote.productPrice} ${orderQuote.quote.currency} (shipping & tax are calculated at checkout).`
                        : `This book will have ${orderQuote.pages.length} page${orderQuote.pages.length === 1 ? "" : "s"}. (Live price unavailable.)`}
                    </div>
                    <button className="btn btn-success btn-sm" onClick={this._confirmOrderPrintedBook} disabled={generating}>
                      Confirm &amp; Prepare the Book
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      style={{ marginLeft: 8 }}
                      onClick={this._cancelOrderPrintedBook}
                      disabled={generating}
                    >
                      Cancel
                    </button>
                  </div>
                ) : null}
                {hasMapPages ? (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={includeMaps}
                        disabled={!!orderQuote}
                        onChange={(e) => {
                          if (!this._markPagesDirty()) return;
                          this.setState({ includeMaps: e.target.checked });
                        }}
                        style={{ marginRight: 6 }}
                      />
                      Include maps
                    </label>
                  </div>
                ) : null}
                {pages.some((p) => p.type === "photo" && p.lowQuality) ? (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ cursor: "pointer" }} title="Leave out the photos marked “Low resolution” — they may print blurry">
                      <input
                        type="checkbox"
                        checked={pages.filter((p) => p.type === "photo" && p.lowQuality).every((p) => p.excluded)}
                        disabled={!!orderQuote}
                        onChange={(e) => this._setExcludeLowRes(e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      Exclude low-res images ({pages.filter((p) => p.type === "photo" && p.lowQuality).length})
                    </label>
                  </div>
                ) : null}
                <div style={{ marginTop: 8 }}>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => this._addCustomPage()} disabled={!!orderQuote}>
                    + Add Custom Page
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" style={{ marginLeft: 8 }} onClick={this._startOver} disabled={!!orderQuote} title="Rebuild the book from the guide's photos, as when first opened">
                    Start over
                  </button>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={pages.some((p) => p.type === "blank" && p.manualCover)}
                      disabled={!!orderQuote}
                      onChange={(e) => this._toggleBlankCover(e.target.checked)}
                      style={{ marginRight: 6 }}
                    />
                    Blank cover (instead of a photo or map)
                  </label>
                </div>
                <div style={{ marginTop: 4, color: "#888", fontSize: 12 }}>
                  {visiblePages.length} page{visiblePages.length === 1 ? "" : "s"} — your arrangement (order, crops,
                  pages left out, custom pages, captions) is kept with the guide; save the guide to keep it for
                  next time.
                </div>
                {orderQuote ? (
                  <div style={{ marginTop: 4, color: "#92400E", fontSize: 12 }}>
                    Editing is locked while confirming your order below — Cancel to make further changes.
                  </div>
                ) : null}
                {showPageInfo ? (
                  <div style={{ marginTop: 10, padding: 10, border: "1px solid #ddd", borderRadius: 6, background: "#f9f9f9" }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>How your pages become a printed book</div>
                    <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                      {this._renderCoverThumb(frontCoverPage, "Front Cover")}
                      {this._renderCoverThumb(backCoverPage, "Back Cover")}
                    </div>
                    <div style={{ fontSize: 12, color: "#555" }}>
                      Your first page is printed on the front cover. Your last page is printed on the back cover. A printed book needs an even number of pages, so with an odd number you choose: add a blank page inside, or make the back cover blank. Inside, the printer binds in white: the inside of each cover and one white sheet at each end, which can't be printed on. So when you open the book you first see two white pages, and your second page is the first right-hand page; after that your pages pair up as left/right spreads, both sides of the paper. At the end, your last inside page is followed by a white sheet and the white inside of the back cover. Printed books have at least 24 pages: a shorter book needs blank pages just before the back cover (you add them with one tap), and is priced as 24 pages. Preview Book shows it exactly this way.
                    </div>
                    {/* Who to contact (2026-10-19): Peecho prints, ships and takes
                        payment, so order problems go to them. */}
                    <div style={{ fontSize: 12, color: "#555", marginTop: 8 }}>
                      Printing, shipping and payment are handled by Peecho, our print partner. For any problem with
                      an order — payment, delivery or print quality — please contact Peecho:{" "}
                      <a href="https://www.peecho.com/contact" target="_blank" rel="noopener noreferrer">peecho.com/contact</a>.
                    </div>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      style={{ marginTop: 8 }}
                      onClick={() => this.setState({ showPageInfo: false })}
                    >
                      Got it
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {pages.length === 0 ? (
              // No photos is no longer a dead end (2026-10-20): a book can be
              // made entirely of custom pages.
              <div>
                <div>
                  No photos found. Add some via "Personalize Guide with Photos" — only places with photos
                  embedded that way show up here. Or make your own book from custom pages.
                </div>
                <button className="btn btn-outline-primary btn-sm" style={{ marginTop: 8 }} onClick={() => this._addCustomPage()}>
                  + Add Custom Page
                </button>
              </div>
            ) : (
              <div style={orderQuote ? { pointerEvents: "none", opacity: 0.5 } : undefined}>
              {(() => {
                let visibleIndex = -1;
                return pages.map((page, index) => {
                if (page.type === "map" && !includeMaps) return null;
                if (!page.excluded) visibleIndex += 1;
                // A designed back cover that's the last page is the back cover
                // even when a blank goes in before it (padPagesToEven).
                const roleLabel = page.excluded
                  ? "Excluded"
                  : page.isBackCover && visibleIndex === visiblePages.length - 1
                  ? "Back Cover"
                  : pageRoleLabel(visibleIndex, totalPadded);
                return (
                  <div className="list-item-card" key={index} style={page.excluded ? { opacity: 0.5 } : undefined}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 6,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: page.excluded ? "#999" : "#3598DB", textTransform: "uppercase" }}>
                          {roleLabel}
                        </div>
                        {page.type === "html" && !page.autoCredits && !page.isBackCover ? (
                          <div
                            style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                            title="Click to rename this page (the name isn't printed)"
                            onClick={() => !this._pagesLocked() && this.setState({ renameIndex: index })}
                          >
                            {page.title || "Untitled page"}
                          </div>
                        ) : (
                          <div style={{ fontWeight: 600 }}>{page.isBackCover ? "Back cover" : page.autoCredits ? this._creditsLabel(page) : this._captionPreview(page, index)}</div>
                        )}
                        {this._renderCoverControls(page, index)}
                        {page.lowQuality ? (
                          <div style={{ fontSize: 11, color: "#C2410C", marginTop: 2 }} title="This photo's resolution may look soft or blurry at full-page print size.">
                            ⚠ Low resolution — may print blurry{lowQualityDetail(page.lowQualityInfo)}
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <FontAwesomeIcon
                          icon={solid("pen")}
                          className="pointer color-blue"
                          title="Edit caption"
                          style={{ marginRight: 10 }}
                          onClick={() => this._editCaption(index)}
                        />
                        <FontAwesomeIcon
                          icon={solid("angles-up")}
                          className="pointer color-blue"
                          title="Move to front"
                          style={{ marginRight: 6, opacity: index === 0 ? 0.3 : 1 }}
                          onClick={() => index > 0 && this._movePageToStart(index)}
                        />
                        <FontAwesomeIcon
                          icon={solid("arrow-up")}
                          className="pointer color-blue"
                          title="Move up one"
                          style={{ marginRight: 10, opacity: index === 0 ? 0.3 : 1 }}
                          onClick={() => index > 0 && this._movePage(index, -1)}
                        />
                        <FontAwesomeIcon
                          icon={solid("arrow-down")}
                          className="pointer color-blue"
                          title="Move down one"
                          style={{ marginRight: 6, opacity: index === pages.length - 1 ? 0.3 : 1 }}
                          onClick={() => index < pages.length - 1 && this._movePage(index, 1)}
                        />
                        <FontAwesomeIcon
                          icon={solid("angles-down")}
                          className="pointer color-blue"
                          title="Move to back"
                          style={{ marginRight: 10, opacity: index === pages.length - 1 ? 0.3 : 1 }}
                          onClick={() => index < pages.length - 1 && this._movePageToEnd(index)}
                        />
                        {this._canDeletePage(page) ? (
                          <FontAwesomeIcon
                            icon={solid("trash")}
                            className="pointer"
                            title="Delete this page"
                            style={{ marginRight: 10, color: "#C2410C" }}
                            onClick={() => this._deletePage(index)}
                          />
                        ) : null}
                        <label
                          title={page.excluded ? "Excluded — click to include in the book" : "Included — click to exclude from the book"}
                          style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                        >
                          <input
                            type="checkbox"
                            checked={!page.excluded}
                            onChange={() => this._toggleIncludePage(index)}
                          />
                        </label>
                      </div>
                    </div>
                    {page.type === "blank" ? (
                      <div style={{ border: "1px dashed #ccc", borderRadius: 4, padding: 20, textAlign: "center", color: "#999", background: "#fafafa" }}>
                        Blank page — nothing prints here
                      </div>
                    ) : page.type === "html" ? (
                      <>
                        {/* Real rendered HTML (same markup buildBookHtml
                            prints and the editor itself produces), not a
                            plain-text placeholder — the list should preview
                            as close to the actual print as every other page
                            type already does. Found 2026-10-03. Square, and
                            rendered at the TRUE print size then scaled down
                            (same technique as PhotoBookFlipViewer.js) rather
                            than a flat unscaled render at a made-up font
                            size — so an image's absolute px width (or any
                            other size-sensitive content) looks proportioned
                            the way it actually will on the real ~794px-wide
                            page, not stretched to fill an arbitrary
                            rectangle. It clips (overflow: hidden) rather
                            than scrolls — a scrollbar here implied content
                            could be scrolled into view when printed, which
                            isn't true (the real page clips silently). The
                            hidden div just below, sized at the real 210mm
                            page's exact content box (210mm minus
                            buildBookHtml's 12mm padding on each side,
                            matching .html-page in functions/index.js
                            exactly), is what actually answers whether
                            content fits — mm is a logical CSS unit so this
                            stays accurate regardless of screen pixel
                            density. */}
                        <div
                          style={{
                            width: COMPACT_PAGE_PX,
                            height: COMPACT_PAGE_PX,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            background: "#fff",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              width: PRINT_PAGE_PX,
                              height: PRINT_PAGE_PX,
                              padding: PRINT_PADDING_PX,
                              boxSizing: "border-box",
                              position: "absolute",
                              top: 0,
                              left: 0,
                              transformOrigin: "top left",
                              transform: `scale(${COMPACT_PAGE_PX / PRINT_PAGE_PX})`,
                              background: "#fff",
                              color: "#222",
                              fontFamily: "system-ui, Arial, sans-serif",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: page.captionHtml || "<span style='color:#999'>Click the pencil icon to write its content.</span>",
                            }}
                          />
                        </div>
                        {page.contentOverflows ? (
                          <div style={{ fontSize: 12, color: "#C2410C", marginTop: 4 }}>
                            ⚠ This text is too long to fit on one printed page — the real book cuts off overflow
                            silently, it doesn't scroll or add a page. Shorten it or split it into another custom
                            page.
                          </div>
                        ) : null}
                        <div
                          ref={(el) => this._measureCustomPageOverflow(index, el)}
                          style={{
                            position: "absolute",
                            visibility: "hidden",
                            left: -9999,
                            top: 0,
                            width: "186mm",
                            height: "186mm",
                            padding: 0,
                            overflow: "hidden",
                            fontFamily: "system-ui, Arial, sans-serif",
                            // 16px — Chromium/Puppeteer's default, matching
                            // .html-page in functions/index.js exactly
                            // (that class sets no font-size of its own), not
                            // the 14px chosen just for the compact visible
                            // preview above.
                            fontSize: 16,
                          }}
                          dangerouslySetInnerHTML={{ __html: page.captionHtml || "" }}
                        />
                      </>
                    ) : page.resolving ? (
                      <div>Loading…</div>
                    ) : page.src ? (
                      // flexWrap — without it, the crop grid got pushed
                      // outside the card's right edge on narrow/small-screen
                      // widths instead of dropping below the image. The
                      // image itself needs minWidth:0 too: a flex item with
                      // width:100% won't shrink below its intrinsic size
                      // otherwise, which is what was forcing the overflow in
                      // the first place. Found 2026-10-03.
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 8 }}>
                        <img
                          src={page.src}
                          // A resized Commons version that doesn't exist falls back to the original.
                          onError={(e) => { if (page.rawUrl && e.currentTarget.src !== page.rawUrl) e.currentTarget.src = page.rawUrl; }}
                          alt=""
                          style={{
                            maxWidth: 280,
                            maxHeight: 280,
                            minWidth: 0,
                            width: "100%",
                            height: 220,
                            objectFit: "cover",
                            objectPosition: page.objectPosition || "center",
                          }}
                        />
                        {this._renderCropGrid(page, index)}
                      </div>
                    ) : (
                      <div style={{ color: "#C2410C" }}>
                        Couldn't load this photo (Google Drive sign-in may have expired, or the
                        file was removed/moved).
                      </div>
                    )}
                  </div>
                );
                });
              })()}
              </div>
            )}
            {/* Jump to the top or bottom of a long page list (2026-10-22). */}
            {pages.length > 6 ? (
              <div style={{ position: "sticky", bottom: 4, display: "flex", justifyContent: "flex-end", gap: 6, pointerEvents: "none" }}>
                {[["arrow-up", "Top", () => this._bodyEl && this._bodyEl.scrollTo({ top: 0, behavior: "smooth" })],
                  ["arrow-down", "Bottom", () => this._bodyEl && this._bodyEl.scrollTo({ top: this._bodyEl.scrollHeight, behavior: "smooth" })]].map(([ic, label, go]) => (
                  <button key={label} title={label === "Top" ? "Go to the first page" : "Go to the last page"} aria-label={label}
                    onClick={go} className="btn btn-sm"
                    style={{ pointerEvents: "auto", width: 38, height: 38, borderRadius: 19, padding: 0, background: "#3598DB", color: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.25)" }}>
                    <FontAwesomeIcon icon={ic === "arrow-up" ? solid("arrow-up") : solid("arrow-down")} />
                  </button>
                ))}
              </div>
            ) : null}
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  guide_info_id: state.guide_meta.guide_info_id,
  user_info: state.user_info,
});

export default connect(mapStateToProps)(PhotoBookPreviewModal);
